import { useState, useMemo } from "react";
import toast from "react-hot-toast";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton } from "@mui/material";
import CustomTextField from "../../../UI/CustomTextField";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { supabase } from "../../../../supabase";
import { useMainContext } from "../../../../context/useMainContext";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
export default function Notes() {
  const { 
    setRows, 
    isGuest, 
    user, 
    currentNoteInfluencer, 
    setCurrentNoteInfluencer, 
    saveUndoData, 
    setLoadingData 
  } = useMainContext();
  const [localText, setLocalText] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);

  const validNotes = useMemo(() => {
    const notesData = currentNoteInfluencer?.internal_reviews?.notes;
    if (!notesData) return [];
    let notes = [];
    if (typeof notesData === "string") {
      try {
        notes = JSON.parse(notesData);
      } catch {
        return [];
      }
    } else if (Array.isArray(notesData)) {
      notes = notesData;
    } else {
      return [];
    }
    return notes.filter((note) => {
      if (!note.createdAt) return false;
      const date = new Date(note.createdAt);
      return !isNaN(date.getTime());
    });
  }, [currentNoteInfluencer?.internal_reviews?.notes]);

  const sortedNotes = useMemo(() => {
    return [...validNotes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [validNotes]);

  const updateNotesInDatabase = async (updatedNotes) => {
    if (!currentNoteInfluencer?.id) return false;
    try {
      setLoadingData(true);
      const { error } = await supabase.from("internal_reviews").upsert(
        {
          influencer_id: currentNoteInfluencer.id,
          notes: updatedNotes,
        },
        { onConflict: "influencer_id" }
      );

      if (error) {
        toast.error("Failed to update notes.");
        return false;
      }

      setCurrentNoteInfluencer((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          internal_reviews: {
            ...(prev.internal_reviews || {}),
            notes: updatedNotes,
          },
        };
      });

      setRows((prevRows) => {
        return prevRows.map((r) => {
          if (r.id === currentNoteInfluencer.id) {
            return {
              ...r,
              internal_reviews: {
                ...(r.internal_reviews || {}),
                notes: updatedNotes,
              },
            };
          }
          return r;
        });
      });

      return true;
    } catch (error) {
      console.error("Error updating notes:", error);
      toast.error("An error occurred while updating notes.");
      return false;
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveNote = async () => {
    if (isGuest) {
      toast.error("Guests cannot view or add notes.");
      return;
    }

    if (!localText.trim()) {
      toast.error("Please write something before saving.");
      return;
    }

    const editingIndex = editingNoteId !== null ? editingNoteId : -1;
    const prevNote = editingIndex >= 0 ? sortedNotes[editingIndex] : null;
    const newNote = {
      text: localText.trim(),
      author: prevNote?.author || user?.name || "Unknown",
      createdAt: prevNote?.createdAt || new Date().toISOString(),
    };

    const previousNotes = [...validNotes];
    let updatedNotes;
    if (editingIndex >= 0) {
      const targetIndex = validNotes.findIndex((note) => 
        note.text === prevNote.text && 
        note.author === prevNote.author && 
        note.createdAt === prevNote.createdAt
      );
      updatedNotes = [...validNotes];
      if (targetIndex >= 0) {
        updatedNotes[targetIndex] = newNote;
      }
    } else {
      updatedNotes = [...validNotes, newNote];
    }

    const success = await updateNotesInDatabase(updatedNotes);
    
    if (success) {
      // Save undo data
      saveUndoData({
        action: editingNoteId ? "Note updated" : "Note added",
        undoFunction: () => updateNotesInDatabase(previousNotes),
        redoFunction: () => updateNotesInDatabase(updatedNotes),
      });

      toast.success(editingNoteId ? "Note updated!" : "Note added!");
      setLocalText("");
      setIsEditMode(false);
      setEditingNoteId(null);
    }
  };

  const handleDeleteNote = async (note) => {
    if (isGuest) {
      toast.error("Guests cannot delete notes.");
      return;
    }
    const updatedNotes = validNotes.filter((n) => n.createdAt !== note.createdAt);
    const success = await updateNotesInDatabase(updatedNotes);
    if (success) {
      toast.success("Note deleted!");
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setLocalText("");
    setEditingNoteId(null);
    setCurrentNoteInfluencer(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleDateString("en-GB");
  };

  return (
    <Dialog 
      open={!!currentNoteInfluencer} 
      onClose={handleCancelEdit}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            height: { xs: "90vh", md: "560px" },
            maxHeight: "90vh"
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Notes
        <IconButton onClick={handleCancelEdit} size="small" aria-label="Close dialog">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
        {(isEditMode || !sortedNotes.length) && (
          <Box sx={{ p: 2 }}>
            <CustomTextField
              fullWidth
              multiline
              rows={3}
              placeholder={editingNoteId ? "Edit your note..." : "Write a new note..."}
              value={localText}
              onChange={(e) => setLocalText(e.target.value)}
              autoFocus
            />
          </Box>
        )}

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            flex: 1,
          }}
        >
          {sortedNotes.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                color: "text.secondary",
                py: 4,
              }}
            >
              No notes yet. Add your first note!
            </Box>
          ) : (
            sortedNotes.map((note, index) => (
              <Box
                key={`note-${note.createdAt}-${index}`}
                sx={{
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                  backgroundColor: index === editingNoteId ? "#f0f0f0" : "transparent",
                }}
              >
                <Typography>{note.text}</Typography>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "text.secondary",
                    }}
                  >
                    {note.author} • {formatDate(note.createdAt)}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleDeleteNote(note)}
                      sx={{ minWidth: "fit-content", px: 0.3 }}
                      color="inherit"
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setLocalText(note.text);
                        setEditingNoteId(index);
                        setIsEditMode(true);
                      }}
                      sx={{ minWidth: "fit-content", px: 0.3 }}
                      color="inherit"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </Button>
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 2 }}>
        {isEditMode || !sortedNotes.length ? (
          <>
            <Button onClick={handleCancelEdit} fullWidth variant="outlined" size="large">
              Discard
            </Button>
            <Button variant="contained" disableElevation onClick={handleSaveNote} fullWidth size="large">
              {editingNoteId ? "Update note" : "Save note"}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => {
              setLocalText("");
              setEditingNoteId(null);
              setIsEditMode(true);
            }}
            fullWidth
            color="primary"
            variant="contained"
            disableElevation
            size="large"
          >
            Add new note
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
